import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Typography } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import { useCart, useResponsive } from "@/hooks";
import type { CartItem } from "@/types";

import { CartDrawerItem } from "./CartDrawerItem";
import { CartEmptyState } from "./CartEmptyState";
import { CartDrawerSkeleton } from "./CartDrawerSkeleton";
import { CartPriceSummary } from "./CartPriceSummary";

const { Text, Title } = Typography;

const CART_DRAWER_BOOTSTRAP_MS = 240;

export const CartDrawer = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const {
    clearCart,
    closeDrawer,
    finalTotal,
    hasItems,
    isDrawerOpen,
    itemCount,
    items,
    removeItem,
    shippingFee,
    total,
    updateItemQuantity,
  } = useCart();
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const [highlightedProductId, setHighlightedProductId] = useState<
    string | null
  >(null);
  const previousItemsRef = useRef<CartItem[]>(items);
  const highlightTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isDrawerOpen || hasBootstrapped) {
      return;
    }

    setIsBootstrapping(true);
    const timeoutId = window.setTimeout(() => {
      setIsBootstrapping(false);
      setHasBootstrapped(true);
    }, CART_DRAWER_BOOTSTRAP_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [hasBootstrapped, isDrawerOpen]);

  useEffect(() => {
    const previousItems = previousItemsRef.current;
    const previousMap = new Map(
      previousItems.map((item) => [item.productId, item.quantity]),
    );
    const changedItem = items.find((item) => {
      const previousQuantity = previousMap.get(item.productId);
      return previousQuantity === undefined || item.quantity > previousQuantity;
    });

    if (changedItem) {
      setHighlightedProductId(changedItem.productId);

      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }

      highlightTimerRef.current = window.setTimeout(() => {
        setHighlightedProductId(null);
      }, 1400);
    }

    previousItemsRef.current = items;
  }, [items]);

  useEffect(
    () => () => {
      if (highlightTimerRef.current) {
        window.clearTimeout(highlightTimerRef.current);
      }
    },
    [],
  );

  const handleCheckout = () => {
    closeDrawer();
    navigate(ROUTES.CHECKOUT);
  };

  const handleContinueShopping = () => {
    closeDrawer();
    navigate(ROUTES.PRODUCTS);
  };

  return (
    <Drawer
      placement="right"
      open={isDrawerOpen}
      closable={false}
      destroyOnClose={false}
      width={isMobile ? "100%" : 432}
      onClose={closeDrawer}
      className="cart-drawer"
      styles={{ body: { padding: 0 } }}
    >
      <div className="cart-drawer__shell">
        <div className="cart-drawer__header">
          <Button
            aria-label="Đóng giỏ hàng"
            className="cart-drawer__back"
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={closeDrawer}
          />
          <div className="cart-drawer__heading">
            <div className="cart-drawer__heading-main">
              <ShoppingCartOutlined />
              <Title level={3}>Giỏ hàng</Title>
            </div>
            <Text className="cart-drawer__count" type="secondary">
              {itemCount} sản phẩm
            </Text>
          </div>
          <div className="cart-drawer__header-spacer" />
        </div>

        <div className="cart-drawer__body">
          {isBootstrapping ? (
            <CartDrawerSkeleton />
          ) : (
            <>
              <AnimatePresence initial={false}>
                {hasItems ? (
                  <motion.div
                    className="cart-drawer__summary"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CartPriceSummary
                      variant="drawer"
                      itemCount={itemCount}
                      total={total}
                      shippingFee={shippingFee}
                      finalTotal={finalTotal}
                    />
                    <Button
                      block
                      size="large"
                      type="primary"
                      onClick={handleCheckout}
                    >
                      Thanh toán ngay
                    </Button>
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <div className="cart-drawer__content">
                <AnimatePresence mode="wait">
                  {!hasItems ? (
                    <motion.div
                      key="empty"
                      className="cart-drawer__empty"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.24 }}
                    >
                      <CartEmptyState
                        variant="drawer"
                        title="Bạn chưa thêm sản phẩm nào"
                        description="Thêm sản phẩm để bắt đầu đơn hàng."
                        primaryAction={
                          <Button
                            block
                            size="large"
                            type="primary"
                            onClick={handleContinueShopping}
                          >
                            Khám phá ngay
                          </Button>
                        }
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="items"
                      className="cart-drawer__list"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      <AnimatePresence initial={false}>
                        {items.map((item) => (
                          <CartDrawerItem
                            key={item.productId}
                            item={item}
                            isHighlighted={
                              item.productId === highlightedProductId
                            }
                            onRemove={removeItem}
                            onQuantityChange={updateItemQuantity}
                          />
                        ))}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>

        <div className="cart-drawer__footer">
          {hasItems ? (
            <div className="cart-drawer__footer-actions">
              <Link to={ROUTES.PRODUCTS} onClick={closeDrawer}>
                <Button icon={<ArrowRightOutlined />} type="text">
                  Tiếp tục mua sắm
                </Button>
              </Link>
              <Button danger type="text" onClick={clearCart}>
                Xóa giỏ hàng
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
};
